import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Annonces aux salaries (Pavel 2026-07-25).
 * - Pour TOUT LE MONDE : recupere l'annonce active a l'ouverture et l'affiche en
 *   pop-up (dans la langue de l'utilisateur) tant qu'il ne l'a pas fermee.
 * - Pour le MANAGER : un bouton flottant "megaphone" ouvre le composer pour
 *   publier une annonce (traduite automatiquement cote serveur).
 * Monte une seule fois dans MainLayout.
 */
export default function Announcements({ user, isManager, showFab = true }) {
  const { language, t } = useLanguage();

  // --- Pop-up d'annonce active ---
  const [ann, setAnn] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/announcements/active`);
        if (!cancelled && res.data && res.data.id) {
          setAnn(res.data);
          setPopupOpen(true);
        }
      } catch (e) { /* silencieux : ne bloque jamais l'ouverture de l'app */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const pick = (obj) => (obj && (obj[language] || obj.fr)) || '';

  const dismiss = async () => {
    setPopupOpen(false);
    try { if (ann?.id) await axios.post(`${API}/announcements/${ann.id}/dismiss`); } catch (e) {}
  };

  // --- Composer (manager) ---
  const [composerOpen, setComposerOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // { announcement, total, read[], unread[] }
  const [history, setHistory] = useState([]);

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${API}/announcements/manager/status`);
      setStatus(res.data);
    } catch (e) { setStatus(null); }
    try {
      const h = await axios.get(`${API}/announcements/history`);
      setHistory(h.data.history || []);
    } catch (e) { setHistory([]); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch (e) { return iso.slice(0, 16).replace('T', ' '); }
  };

  useEffect(() => {
    if (composerOpen && isManager) loadStatus();
  }, [composerOpen, isManager]);

  const removeAnnouncement = async () => {
    if (!status?.announcement?.id) return;
    try {
      await axios.delete(`${API}/announcements/${status.announcement.id}`);
      toast.success(t('announcementRemoved'));
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || t('error')); }
  };

  const publish = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/announcements`, { title: title.trim(), message: message.trim() });
      toast.success(t('announcementPublished'));
      setTitle('');
      setMessage('');
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Pop-up d'annonce (tout le monde) */}
      {ann && (
        <Dialog open={popupOpen} onOpenChange={(o) => { if (!o) dismiss(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-blue-600" />
                {pick(ann.title_i18n) || t('announcement')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-1">
              <p className="whitespace-pre-line text-sm text-gray-700 leading-relaxed">
                {pick(ann.message_i18n)}
              </p>
              {ann.created_by_name && (
                <p className="text-xs text-gray-400">— {ann.created_by_name}</p>
              )}
              <Button onClick={dismiss} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                {t('understood')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Bouton flottant + composer (manager uniquement, tableau de bord seulement) */}
      {isManager && showFab && (
        <>
          <button
            onClick={() => setComposerOpen(true)}
            title={t('announceToStaff')}
            aria-label={t('announceToStaff')}
            className="fixed right-4 bottom-40 lg:right-6 lg:bottom-24 z-40 flex items-center justify-center w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-colors"
            data-testid="announce-fab"
          >
            <Megaphone className="w-6 h-6" />
          </button>

          <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-blue-600" />
                  {t('announceToStaff')}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-1">
                {/* Suivi de lecture de l'annonce en cours */}
                {status && status.announcement && (
                  <div className="rounded-lg border bg-gray-50 p-3 text-sm">
                    {(pick(status.announcement.title_i18n)) && (
                      <p className="font-semibold text-gray-800">{pick(status.announcement.title_i18n)}</p>
                    )}
                    <p className="whitespace-pre-line text-gray-700 mb-2">{pick(status.announcement.message_i18n)}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-700">
                        {t('seenBy')} {status.read.length}/{status.total}
                      </span>
                      <button onClick={removeAnnouncement} className="text-xs text-red-500 hover:underline">
                        {t('removeAnnouncement')}
                      </button>
                    </div>
                    {status.unread.length > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        <span className="font-medium">{t('notReadYet')} :</span>{' '}
                        {status.unread.map((u) => u.name).join(', ')}
                      </p>
                    )}
                    {status.read.length > 0 && (
                      <p className="mt-1 text-xs text-green-600">
                        <span className="font-medium">{t('read')} :</span>{' '}
                        {status.read.map((u) => u.name).join(', ')}
                      </p>
                    )}
                    <p className="mt-2 text-[11px] text-gray-400">{t('publishReplaces')}</p>
                  </div>
                )}
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('announcementTitle')}
                  maxLength={120}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('announcementMessage')}
                  rows={5}
                  maxLength={2000}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <p className="text-xs text-gray-400">{t('announcementAutoTranslate')}</p>
                <Button
                  onClick={publish}
                  disabled={sending || !message.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {sending ? '…' : t('publishAnnouncement')}
                </Button>

                {/* Historique des annonces (texte envoye + date + lectures) */}
                {history && history.length > 0 && (
                  <div className="mt-2 border-t pt-2">
                    <p className="text-xs font-semibold text-gray-500 mb-1">{t('announcementHistory')}</p>
                    <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                      {history.map((h) => (
                        <div key={h.id} className="text-xs border rounded-lg p-2 bg-white">
                          <div className="flex items-center justify-between text-gray-400">
                            <span>{fmtDate(h.created_at)}{h.active ? ' •' : ''}</span>
                            <span>{t('seenBy')} {h.read_count}/{h.total}</span>
                          </div>
                          <p className="whitespace-pre-line text-gray-700 mt-1">{pick(h.message_i18n)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
}
